import { useEffect, useState, type ReactElement } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { hasSession } from "../../state/session";
import { Button, StickyButton } from "../Button";
import { ThemeToggleButton } from "../ThemeToggleButton";
import { NavMenuControlCluster } from "./NavMenuControlCluster";
import { MarketingNavMenu } from "./MarketingNavMenu";
import { DrawerInspectorNavRow, DrawerInspectorNavRowList } from "../drawer/DrawerInspectorNavRow";
import { LotScannerLogo } from "./LotScannerLogo";
import { isAccountAppRoute } from "./accountAppRoutes";
import { scrollToMarketingSection } from "./marketingNavAnchor";
import styles from "./MarketingTopNav.module.css";

import { COMPACT_NAV_MEDIA_QUERY } from "../navBreakpoint";

/**
 * Sticky marketing nav (rendered inside `#app-top-nav` by RootLayout).
 *
 * On `/dashboard`, `/properties`, `/team`, and `/compare/lots` the right-side
 * nav links are replaced by a single theme toggle (sun / moon). Other
 * marketing routes keep the toggle in `nav-actions` on desktop (>980px).
 *
 * On compact viewports (≤980px — where the marketing sections collapse to
 * single column) the link row is replaced by a menu toggle that opens a
 * full-screen panel dropping from the nav bar (fill-pure, text/default).
 * The icon swaps to close (X) while open; the theme toggle slides out from
 * behind it to the left (16px gap) only while the menu is open.
 *
 * On the landing route, once the hero search scrolls out of view the sticky
 * bar promotes Scan lot to primary and adds View demo as outline — the same
 * hierarchy swap that desktop shows in `nav-actions`.
 *
 * `Link` is used for the "View demo" CTA so the marketing → viewer
 * transition stays inside the SPA. Full-document hash anchors (#whats-in,
 * #pricing, …) keep plain `<a>` because hash scrolling already works
 * without router involvement.
 */
export function MarketingTopNav(): ReactElement {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isAccountApp = isAccountAppRoute(pathname);
  const isLanding = pathname === "/";
  const [scanCtaVisible, setScanCtaVisible] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isCompactNav, setIsCompactNav] = useState(() =>
    typeof window.matchMedia === "function"
      ? window.matchMedia(COMPACT_NAV_MEDIA_QUERY).matches
      : false
  );

  const showStickyCta = !isAccountApp && isCompactNav;

  // Close the drawer on route change (link taps navigate before the close
  // click handler's state flush is observable) and when the viewport grows
  // past the compact breakpoint, so the sheet never lingers on desktop.
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(COMPACT_NAV_MEDIA_QUERY);
    const onChange = (): void => {
      setIsCompactNav(mq.matches);
      if (!mq.matches) setMenuOpen(false);
    };
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (!showStickyCta) return;
    document.body.classList.add("marketing-sticky-cta");
    return () => {
      document.body.classList.remove("marketing-sticky-cta");
    };
  }, [showStickyCta]);

  useEffect(() => {
    if (!isLanding || typeof IntersectionObserver !== "function") {
      setScanCtaVisible(false);
      return;
    }
    // The hero search mounts inside the lazy MarketingPage chunk, so it may
    // not exist on the nav's first render. Poll briefly until it appears.
    let observer: IntersectionObserver | null = null;
    let pollHandle = 0;
    let cancelled = false;

    const attach = (): void => {
      if (cancelled) return;
      const heroSearch = document.getElementById("hero-search");
      if (!heroSearch) {
        pollHandle = window.setTimeout(attach, 250);
        return;
      }
      observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            setScanCtaVisible(!entry.isIntersecting);
          }
        },
        { threshold: 0 }
      );
      observer.observe(heroSearch);
    };
    attach();

    return () => {
      cancelled = true;
      window.clearTimeout(pollHandle);
      observer?.disconnect();
      setScanCtaVisible(false);
    };
  }, [isLanding]);

  const scrollToHeroSearch = (): void => {
    const input = document.querySelector<HTMLInputElement>(
      "#hero-search input[type='search']"
    );
    window.scrollTo({ top: 0, behavior: "smooth" });
    window.setTimeout(() => input?.focus(), 500);
  };

  const goToMenuSection = (sectionId: string): void => {
    setMenuOpen(false);
    const run = (): void => {
      if (pathname === "/") {
        scrollToMarketingSection(sectionId);
        return;
      }
      void navigate(`/#${sectionId}`);
    };
    requestAnimationFrame(() => requestAnimationFrame(run));
  };

  const goToAccount = (): void => {
    setMenuOpen(false);
    void navigate(hasSession() ? "/dashboard" : "/login");
  };

  const desktopLandingCtas = isLanding ? (
    <>
      <div
        className={
          scanCtaVisible
            ? "nav-compact-scan-slot show"
            : "nav-compact-scan-slot"
        }
        aria-hidden={!scanCtaVisible}
      >
        <Button
          as={Link}
          to="/viewer"
          variant="outline"
          className="nav-compact-scan"
          tabIndex={scanCtaVisible ? 0 : -1}
        >
          View demo
        </Button>
      </div>
      {scanCtaVisible ? (
        <Button type="button" variant="primary" onClick={scrollToHeroSearch}>
          Scan lot
        </Button>
      ) : (
        <Button as={Link} to="/viewer" variant="primary">
          View demo
        </Button>
      )}
    </>
  ) : null;

  const renderStickyCtaBar = (): ReactElement =>
    isLanding ? (
      scanCtaVisible ? (
        <StickyButton layout="sideBySide">
          <Button as={Link} to="/viewer" variant="outline">
            View demo
          </Button>
          <Button type="button" variant="primary" onClick={scrollToHeroSearch}>
            Scan lot
          </Button>
        </StickyButton>
      ) : (
        <StickyButton layout="stacked">
          <Button as={Link} to="/viewer" variant="primary">
            View demo
          </Button>
        </StickyButton>
      )
    ) : (
      <StickyButton layout="stacked">
        <Button as={Link} to="/viewer" variant="primary">
          View demo
        </Button>
      </StickyButton>
    );

  const stickyCtaPortal =
    showStickyCta && typeof document !== "undefined"
      ? createPortal(
          <div className={styles.marketingStickyCtaShell}>
            {renderStickyCtaBar()}
          </div>,
          document.body
        )
      : null;

  return (
    <>
      <nav className="nav">
        <div className="container nav-inner">
          <Link to="/" className="logo" aria-label="LotScan home">
            <LotScannerLogo />
          </Link>
          {isAccountApp ? (
            <ThemeToggleButton />
          ) : (
            <>
              <ul className="nav-links">
                <li>
                  <a href="#whats-in">What's included</a>
                </li>
                <li>
                  <a href="#pricing">Pricing</a>
                </li>
                <li>
                  <a href="#faq">FAQ</a>
                </li>
                <li>
                  {/*
                    Account affordance. Reads localStorage at render; RootLayout
                    re-renders the nav on every route change so the label tracks
                    sign-in state without extra wiring.
                  */}
                  <Link to={hasSession() ? "/dashboard" : "/login"}>
                    {hasSession() ? "Dashboard" : "Sign in"}
                  </Link>
                </li>
              </ul>

              <div className="nav-actions">
                {desktopLandingCtas}
                {!isLanding ? <ThemeToggleButton /> : null}
                {!isLanding ? (
                  <Button as={Link} to="/viewer" variant="primary">
                    View demo
                  </Button>
                ) : null}
              </div>

              {/* Compact (≤980px): theme toggle slides out from behind the menu icon. */}
              <div className="nav-compact">
                <NavMenuControlCluster
                  menuOpen={menuOpen}
                  onMenuToggle={() => setMenuOpen((open) => !open)}
                />
              </div>

              <MarketingNavMenu open={menuOpen} onClose={() => setMenuOpen(false)}>
                <DrawerInspectorNavRowList>
                  <DrawerInspectorNavRow
                    label="What's included"
                    onNavigate={() => goToMenuSection("whats-in")}
                  />
                  <DrawerInspectorNavRow
                    label="Pricing"
                    onNavigate={() => goToMenuSection("pricing")}
                  />
                  <DrawerInspectorNavRow
                    label="FAQ"
                    onNavigate={() => goToMenuSection("faq")}
                  />
                  <DrawerInspectorNavRow
                    label={hasSession() ? "Dashboard" : "Sign in"}
                    onNavigate={goToAccount}
                  />
                </DrawerInspectorNavRowList>
              </MarketingNavMenu>
            </>
          )}
        </div>
      </nav>
      {stickyCtaPortal}
    </>
  );
}
